import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import useAuthContext from '../../../contexts/AuthContext';
import useErrorMessage from '../../../utils/ErrorMessage';
import { indicators } from '../../../utils/constants/misc';
import { SimpleDonutChart } from './SimpleDonutChart';
import useHandleResize from '../../../utils/HandleResize';

const IndicatorV2 = ({ purpose }) => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const [, setIsLoading] = useState(false);
  const [statsData, setStatsData] = useState();
  const [stats, setstats] = useState(0);
  const { message } = useErrorMessage();

  const { width } = useHandleResize();
  const isXSScreen = width < 992;

  const generateData = (f, v) => {
    let total = 0;
    for (let i = 0; i < f.length; i += 1) {
      total += f[i]?.[v];
    }
    return f.map((d) => ({
      title: v,
      total,
      item: d._id,
      count: d?.[v],
      percent: d?.[v] === 0 ? 0 : d?.[v] / total
    }));
  };

  const prepareData = async (data) => {
    let d1;
    let d2;
    let d3;
    switch (purpose) {
      case 'transformationRate':
        d1 = await generateData(data, 'value1');
        d2 = await generateData(data, 'value2');
        setStatsData([d1, d2]);
        break;
      case 'unpaidRate':
        d1 = await generateData(data, 'value1');
        d2 = await generateData(data, 'value2');
        d3 = await generateData(data, 'value3');
        setStatsData([d1, d2, d3]);
        break;
      case 'incompleteFiles':
        d1 = await generateData(data, 'value1');
        d2 = await generateData(data, 'value2');
        setStatsData([d1, d2]);
        break;
      default:
        break;
    }
  };

  const getStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/dashboards/${purpose}/detail`
      });
      const data1 = await dispatchAPI('GET', {
        url: `/dashboards/${purpose}`
      });
      setstats(data1.data);
      prepareData(data);
    } catch (e) {
      message(e);
    }
    setIsLoading(false);
    // eslint-disable-next-line
  }, [purpose]);

  useEffect(() => {
    (async () => {
      await getStats();
    })();
    // eslint-disable-next-line
  }, [getStats]);

  const indicatorsColors = {
    transformationRate: 'var(--textColor)',
    unpaidRate: 'var(--addColor)',
    incompleteFiles: 'var(--textColorA015)',
    claimsRate: 'var(--secondaryColor)'
  };

  return (
    <Card
      bordered={false}
      headStyle={{
        padding: 0,
        textAlign: 'left',
        color: indicatorsColors[purpose]
      }}
      title={t(`dashboards.${purpose}.title`)}
      className="indicator-card indicator-card-large"
    >
      {purpose === 'claimsRate' ? (
        <div
          style={{
            display: 'flex',
            height: 272,
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Typography.Text>En cours de développement</Typography.Text>
        </div>
      ) : (
        <>
          <Typography.Text>
            {`${t(`dashboards.${purpose}.rate`)} : 
              ${(stats && stats.rate?.toFixed(2)) || 0}%`}
          </Typography.Text>
          <div
            style={{
              display: 'flex',
              flexFlow: isXSScreen ? 'column nowrap' : 'row wrap',
              margin: 5
            }}
          >
            {statsData &&
              statsData[0].length &&
              statsData?.map((item) => (
                <div
                  style={{ width: `${100 / statsData.length}%` }}
                  key={item[0].title}
                >
                  <SimpleDonutChart
                    title={t(`dashboards.${purpose}.${item[0].title}`)}
                    total={item[0].total}
                    data={item}
                  />
                </div>
              ))}
          </div>
        </>
      )}
    </Card>
  );
};

IndicatorV2.propTypes = {
  purpose: PropTypes.oneOf(indicators).isRequired
};

export { IndicatorV2 };
